import { useState, type FormEvent } from "react";
import { useApi } from "../lib/useApi";
import { ApiError, type SubscriptionPreview } from "../lib/api";
import { Modal } from "./Modal";

/**
 * Re-points an organization at a different ChargeBee subscription. The staff user enters the target
 * subscription id and clicks Check, which previews it against ChargeBee (does it exist, does it belong
 * to this org's ChargeBee customer, and its currency/plan/seats/status). The change can only be
 * confirmed when the subscription exists AND belongs to the org's customer — the server re-validates
 * the same invariant on commit. Used e.g. to migrate a customer from a USD subscription to a new AUD one
 * (ChargeBee can't change a subscription's currency in place, so finance creates a fresh one).
 */
export function ChangeSubscriptionDialog({ orgId, orgName, currentSubscriptionId, onClose, onDone }: {
  orgId: string;
  orgName: string;
  currentSubscriptionId: string | null;
  onClose: () => void;
  onDone?: () => void;
}) {
  const api = useApi();
  const [subscriptionId, setSubscriptionId] = useState("");
  const [preview, setPreview] = useState<SubscriptionPreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = subscriptionId.trim();
  const unchanged = trimmed !== "" && trimmed === (currentSubscriptionId ?? "");
  const canApply = preview?.exists === true && preview.belongsToCustomer === true;

  // Any edit invalidates a prior preview so a stale "ok to apply" can't be confirmed for a different id.
  function onInput(value: string) {
    setSubscriptionId(value);
    setPreview(null);
    setError(null);
  }

  async function check() {
    if (trimmed === "") { setError("Enter a ChargeBee subscription id."); return; }
    setBusy(true);
    setError(null);
    try {
      setPreview(await api.previewOrganizationSubscription(orgId, trimmed));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setBusy(false);
    }
  }

  async function apply() {
    setBusy(true);
    setError(null);
    try {
      await api.setOrganizationSubscriptionId(orgId, trimmed);
      onDone?.();
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : `${err}`);
    } finally {
      setBusy(false);
    }
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (canApply) { void apply(); } else { void check(); }
  }

  return (
    <Modal title={`Change subscription — ${orgName}`} onClose={onClose}>
      <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 460 }}>
        <div>
          <div className="rail-label" style={{ marginBottom: 8 }}>Current subscription</div>
          <div className="mono" style={{ fontSize: 12 }}>
            {currentSubscriptionId || <span className="faint">— (none)</span>}
          </div>
        </div>

        <div>
          <div className="rail-label" style={{ marginBottom: 8 }}>New subscription id</div>
          <input
            type="text"
            placeholder="e.g. AzZRY4VOZcwEyF2wv"
            value={subscriptionId}
            onChange={(e) => onInput(e.target.value)}
            spellCheck={false}
            autoFocus
            style={{ width: "100%", fontSize: 12, padding: "8px 10px" }}
          />
          <div className="hint" style={{ marginTop: 5 }}>
            Must belong to this organization's ChargeBee customer. Check it before applying — billing,
            seats and entitlements switch to it immediately.
          </div>
          {unchanged && (
            <div className="hint" style={{ color: "var(--red)", marginTop: 5 }}>
              This is already the organization's current subscription.
            </div>
          )}
        </div>

        {preview && (
          preview.exists && preview.belongsToCustomer ? (
            <>
              <dl className="spec">
                <dt>currency</dt>
                <dd className="mono">{preview.currencyCode ?? "—"}</dd>
                <dt>plan</dt>
                <dd className="mono">{preview.planId ?? "—"}</dd>
                <dt>seats</dt>
                <dd className="mono">{preview.quantity ?? "—"}</dd>
                <dt>status</dt>
                <dd className="mono">{preview.status ?? "—"}</dd>
              </dl>
              <div className="callout warn">
                <span className="mk">!</span>
                <span style={{ fontSize: 11 }}>
                  Re-point <b>{orgName}</b> to <b className="mono">{trimmed}</b>
                  {currentSubscriptionId ? <> (from <b className="mono">{currentSubscriptionId}</b>)</> : null}.
                  Billing, seats and entitlements switch to this subscription immediately.
                </span>
              </div>
            </>
          ) : (
            <div className="callout error">
              <span className="mk">err</span>
              <span>
                {!preview.exists
                  ? "No subscription with that id exists in ChargeBee."
                  : "That subscription belongs to a different ChargeBee customer, so it can't be linked to this organization."}
              </span>
            </div>
          )
        )}

        {error && (
          <div className="callout error"><span className="mk">err</span><span>{error}</span></div>
        )}

        <div className="btn-row" style={{ justifyContent: "flex-end" }}>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onClose}>Cancel</button>
          {canApply ? (
            <button type="submit" className={"btn btn-primary btn-sm" + (busy ? " loading" : "")} disabled={busy}>
              Confirm change
            </button>
          ) : (
            <button
              type="submit"
              className={"btn btn-primary btn-sm" + (busy ? " loading" : "")}
              disabled={busy || trimmed === "" || unchanged}
            >
              Check
            </button>
          )}
        </div>
      </form>
    </Modal>
  );
}
